import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateLeadCaptureSessionRequest, FormAnswer, LeadCaptureSession, LeadCaptureSessionResponse, LeadCaptureSessionStatus } from '@troia-v3/types';

import { captureApi } from '../services/captureApi';

export const LEAD_CAPTURE_KEYS = {
  options: ['lead-capture-options'] as const,
  session: (uuid: string) => ['lead-capture-session', uuid] as const,
};

const TERMINAL: ReadonlySet<LeadCaptureSessionStatus> = new Set(['done', 'cancelled']);
/** Reserva do socket (D14): enquanto a sessão não terminou, refetch a cada 5s. */
export const CAPTURE_POLL_INTERVAL_MS = 5000;

export const useLeadCaptureOptions = (enabled: boolean) =>
  useQuery({
    queryKey: LEAD_CAPTURE_KEYS.options,
    queryFn: () => captureApi.getOptions(),
    enabled,
    staleTime: 60 * 1000,
  });

export const useLeadCaptureSession = (uuid: string | null) =>
  useQuery({
    queryKey: LEAD_CAPTURE_KEYS.session(uuid ?? ''),
    queryFn: () => captureApi.getSession(uuid as string),
    enabled: Boolean(uuid),
    refetchInterval: (query) => {
      const status = query.state.data?.session.status;
      return status && TERMINAL.has(status) ? false : CAPTURE_POLL_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
  });

/** Grava a sessão devolvida por create/cancel/skip/complete no cache (sem esperar o próximo poll). */
const useSessionCacheWriter = () => {
  const queryClient = useQueryClient();
  return (next: LeadCaptureSession | LeadCaptureSessionResponse): void => {
    const session = 'session' in next ? next.session : next;
    queryClient.setQueryData<LeadCaptureSessionResponse>(LEAD_CAPTURE_KEYS.session(session.uuid), (prev) =>
      'session' in next ? next : { session, qrDataUrl: prev?.qrDataUrl ?? '', url: prev?.url ?? '' });
    queryClient.invalidateQueries({ queryKey: ['seller-dashboard'] });
  };
};

export const useCreateCaptureSession = () => {
  const write = useSessionCacheWriter();
  return useMutation({ mutationFn: (body: CreateLeadCaptureSessionRequest) => captureApi.createSession(body), onSuccess: write });
};
export const useCancelCaptureSession = () => {
  const write = useSessionCacheWriter();
  return useMutation({ mutationFn: (uuid: string) => captureApi.cancel(uuid), onSuccess: write });
};
export const useSkipCaptureForm = () => {
  const write = useSessionCacheWriter();
  return useMutation({ mutationFn: (uuid: string) => captureApi.skipForm(uuid), onSuccess: write });
};
export const useCompleteCapture = () => {
  const write = useSessionCacheWriter();
  return useMutation({ mutationFn: (uuid: string) => captureApi.complete(uuid), onSuccess: write });
};
export const useSubmitCaptureForm = () => {
  const write = useSessionCacheWriter();
  return useMutation({
    mutationFn: ({ uuid, answers }: { uuid: string; answers: FormAnswer[] }) => captureApi.submitForm(uuid, answers),
    onSuccess: write,
  });
};
